// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { Typography } from "@insite/mobius/index";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import QtyField, { QtyFieldStyles } from "@sc/App/Components/Qty/QtyField";
import { interactionPages } from "@sc/XDL/constants";
import { xMask } from "@sc/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    label?: string;
    // XNG-Changes
    extendedStyles?: QtyFieldStyles;
    showActions?: boolean;
    displayTooltip?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
    isInterActionPage: interactionPages.includes(getCurrentPage(state).type),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Change
    showActions = true,
    displayTooltip,
    isInterActionPage,
}) => {
    // Xng-Changes
    const minimumOrderQty = Number(cartLine.properties.minimumOrderQty);
    const multipleSaleQty = Number(cartLine.properties.multipleSaleQty);

    const minQty = Math.max(minimumOrderQty || 0, 1, multipleSaleQty && multipleSaleQty > 1 ? multipleSaleQty : 1);

    const showQtyTooltip =
        (displayTooltip || isInterActionPage) &&
        ((minimumOrderQty && minimumOrderQty > 1) || (multipleSaleQty && multipleSaleQty > 1));

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        // XNG-Change
        let changeValue = xMask.qty(`${inputQty}`);
        const quantityValue = Number(changeValue);
        if (multipleSaleQty && multipleSaleQty > 1 && quantityValue % multipleSaleQty > 0) {
            changeValue = (Math.ceil(quantityValue / multipleSaleQty) * multipleSaleQty).toString();
        }
        if (Number(changeValue) < minimumOrderQty) {
            changeValue = `${minimumOrderQty}`;
        }

        const possibleNumber = Number(changeValue);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChange(possibleNumber);
        }
    };

    // XNG-Changes
    const xChangeHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value));
        changeHandler(event);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // XNG-Changes
    const xBlurHandler = (event: any) => {
        const value = event?.currentTarget?.value;
        if (value && Number(xMask.qty(value)) === 0) {
            event.currentTarget.value = cartLine.qtyOrdered;
            changeHandler(event);
            return;
        }
        blurHandler();
    };
    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    // XNG-Changes: QtyField
    if (editable && !isOutOfStock(cartLine)) {
        return (
            <QtyField
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                onBlur={xBlurHandler}
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                extendedStyles={extendedStyles}
                data-test-selector="cartline_qty"
                label={label ?? translate("QTY")}
                min={minQty}
                max={9999}
                onClick={event => event.currentTarget.focus()}
                showActionButton={showActions}
                showMultipleSaleQtyTooltip={!!showQtyTooltip}
                minimumOrderQty={minimumOrderQty}
                multipleSaleQty={multipleSaleQty}
            />
        );
    }

    return (
        <QtyField
            extendedStyles={extendedStyles}
            value={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
            editable={false}
            showMultipleSaleQtyTooltip={!!showQtyTooltip}
            minimumOrderQty={minimumOrderQty}
            multipleSaleQty={multipleSaleQty}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
